import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ArrowUpCircle, ArrowDownCircle, X, Trophy, Clock, Eye, History, AlertTriangle, Crown, HeartHandshake, ChevronRight, Scale } from "lucide-react";

const CLUB_EMOJI = { Analistas: "🧠", Diplomatas: "🤝", Sentinelas: "🛡️", Exploradores: "🚀" };
const CLUB_COLOR = { Analistas: "#A855F7", Diplomatas: "#22C55E", Sentinelas: "#3B82F6", Exploradores: "#EAB308" };
const CLUBS = ["Analistas", "Diplomatas", "Sentinelas", "Exploradores"];

const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "sem hora";

export default function Roster() {
  const [data, setData] = useState(null);
  const [rank, setRank] = useState([]);
  const [events, setEvents] = useState([]);
  const [promote, setPromote] = useState(null);
  const [withdraw, setWithdraw] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [volunteers, setVolunteers] = useState({});

  const refresh = async () => {
    try {
      const [r, a] = await Promise.all([
        api.get("/league/roster"),
        api.get("/rankings/accountability"),
      ]);
      setData(r.data);
      setRank(a.data || []);
      try {
        const e = await api.get("/league/status-events");
        setEvents(e.data || []);
        setIsAdmin(true);
      } catch { setIsAdmin(false); }
      try {
        const v = await api.get("/applications/leadership-candidates");
        const map = {};
        (v.data.clubs || []).forEach((row) => {
          map[row.club] = {};
          row.candidates.forEach((p) => {
            if (p.name) map[row.club][p.name] = p.interest;
          });
        });
        setVolunteers(map);
      } catch { setVolunteers({}); }
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { refresh(); }, []);

  if (!data) return <div className="kw-overline">A carregar…</div>;
  const c = data.counts;
  const allVolunteers = Object.assign({}, ...Object.values(volunteers));

  return (
    <div className="space-y-8 kw-fade-up" data-testid="roster-page">
      <div>
        <div className="kw-overline mb-2">Edição atual · {data.weeks_total} semanas</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
          Plantel da <span className="text-[#E50000]">Liga</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-3xl">
          {data.quota_active} concorrentes por clube. Quem entra depois do
          arranque fica <b className="text-white">Observador</b>: reporta e constrói a sua accountability pessoal
          sem contar para o campeonato.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="roster-counts">
        {[
          { k: "competing", label: "A coopetir", icon: Trophy, color: "#22C55E" },
          { k: "observer", label: "Observadores", icon: Eye, color: "#737373" },
        ].map((x) => (
          <div key={x.k} className="kw-card p-4" data-testid={`count-${x.k}`}>
            <x.icon className="w-4 h-4 mb-2" style={{ color: x.color }} />
            <div className="font-display text-4xl font-black" style={{ color: x.color }}>{c[x.k]}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">{x.label}</div>
          </div>
        ))}
      </div>

      {data.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="roster-alerts">
          {data.alerts.map((a) => (
            <div key={a.club} className="kw-card p-4 border-l-2 border-[#E50000] flex flex-wrap items-center gap-3"
              data-testid={`alert-${a.club}`}>
              <AlertTriangle className="w-5 h-5 text-[#E50000] shrink-0" />
              <div className="flex-1 min-w-[260px] text-sm text-[#A3A3A3]">
                <b className="text-white">{CLUB_EMOJI[a.club]} {a.club} tem {a.free_slots} vaga(s) aberta(s).</b>
                {a.expelled.length > 0 && <> Saíram: {a.expelled.join(", ")}.</>}{" "}
                O próximo na fila é <b className="text-white">{a.next_substitute.name}</b>
                {a.next_substitute.mbti_answered_at && <> (MBTI {fmt(a.next_substitute.mbti_answered_at)})</>}.
                {a.queue.length > 1 && <span className="text-[#555]"> Depois: {a.queue.slice(1).join(", ")}</span>}
              </div>
              {isAdmin && (
                <button onClick={() => setPromote({
                  player: a.next_substitute, club: a.club,
                  competing: (data.by_club[a.club]?.competing) || [],
                })}
                  className="kw-btn-primary !py-2 !px-4 text-[10px] inline-flex items-center gap-1.5 shrink-0"
                  data-testid={`alert-promote-${a.club}`}>
                  <ArrowUpCircle className="w-3.5 h-3.5" /> Promover {a.next_substitute.name.split(" ")[0]}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <LeaguePresident data={data} isAdmin={isAdmin} onDone={refresh} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {CLUBS.map((club) => {
          const row = data.by_club[club] || { competing: [], substitute: [], observer: [] };
          const over = row.competing.length > data.quota_active;
          const short = row.competing.length < data.quota_active;
          return (
            <div key={club} className="kw-card p-0" data-testid={`roster-${club}`}>
              <div className="p-3 border-b border-[#222] flex items-center gap-2">
                <span>{CLUB_EMOJI[club]}</span>
                <span className="font-display font-black uppercase text-sm flex-1">{club}</span>
                <span className={`text-[10px] font-bold ${over ? "text-[#E50000]" : short ? "text-[#EAB308]" : "text-[#22C55E]"}`}>
                  {row.competing.length}/{data.quota_active}
                </span>
              </div>
              {short && (
                <div className="px-3 py-1.5 bg-[#EAB308]/10 text-[9px] uppercase tracking-widest text-[#EAB308]">
                  Faltam {data.quota_active - row.competing.length} para fechar o plantel
                </div>
              )}
              <Leadership people={row.leadership || []} club={club} isAdmin={isAdmin} onDone={refresh}
                volunteers={allVolunteers}
                outsidePool={CLUBS.filter((x) => x !== club).flatMap((x) => {
                  const r = data.by_club[x] || {};
                  return [...(r.observer || []), ...(r.substitute || []), ...(r.competing || [])]
                    .map((p) => ({ ...p, own_club: x }));
                })}
                pool={[...(row.observer || []), ...(row.substitute || []), ...(row.competing || [])]} />
              <Group title="A coopetir" color="#22C55E" people={row.competing} onSince={isAdmin ? refresh : null}
                onWithdraw={isAdmin ? (p) => setWithdraw({ player: p, club }) : null} />
              <Group title="Observadores" color="#737373" people={row.observer} collapsed
                action={isAdmin ? (p) => setPromote({ player: p, club, competing: row.competing }) : null}
                onRole={isAdmin ? refresh : null} />
            </div>
          );
        })}
      </div>

      {(() => {
        const row = data.by_club["Sem clube"];
        const orphans = row?.competing || [];
        const observers = (row?.observer || []).length;
        if (!orphans.length) return null;
        return (
          <div className="kw-card p-0 border-[#EAB308]" data-testid="roster-sem-clube">
            <div className="p-3 border-b border-[#EAB308]/40 flex items-center gap-2 bg-[#EAB308]/10">
              <AlertTriangle className="w-4 h-4 text-[#EAB308]" />
              <span className="font-display font-black uppercase text-sm flex-1 text-[#EAB308]">
                No plantel mas fora de todos os clubes · MBTI em falta
              </span>
              <span className="text-[10px] font-bold text-[#EAB308]">{orphans.length}</span>
            </div>
            <div className="px-3 py-2 text-xs text-[#A3A3A3]">
              O clube vem do MBTI. Estes consultores estão a coopetir mas, sem MBTI na ficha, não contam
              para nenhum clube nem para os rankings — preenche o MBTI em Consultores.
              {observers ? ` (há ainda ${observers} observadores sem MBTI)` : ""}
            </div>
            <Group title="A coopetir sem MBTI" color="#EAB308" people={orphans}
                onWithdraw={isAdmin ? (p) => setWithdraw({ player: p, club: "" }) : null} />
          </div>
        );
      })()}


      {isAdmin && <CardsPanel roster={data} onDone={refresh} />}

      <div>
        <div className="kw-overline mb-2">Observadores</div>
        <h2 className="font-display text-3xl font-black uppercase tracking-tight mb-1">
          Ranking <span className="text-[#3B82F6]">Accountability</span>
        </h2>
        <p className="text-sm text-[#A3A3A3] mb-4 max-w-2xl">
          Disciplina acima de tudo: ordenado por semanas reportadas e depois pelos pontos pessoais.
          Não conta para o campeonato.
        </p>
        <div className="kw-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="accountability-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#737373] border-b border-[#222]">
                <th className="p-3 w-12">#</th><th className="p-3">Consultor</th><th className="p-3">Clube</th>
                <th className="p-3">Estado</th><th className="p-3 text-right">Semanas</th>
                <th className="p-3 text-right">Pontos pessoais</th><th className="p-3 text-right">Não reportou</th>
              </tr>
            </thead>
            <tbody>
              {rank.map((s) => (
                <tr key={s.player_id} className="border-b border-[#161616] hover:bg-[#111]" data-testid={`acc-row-${s.player_id}`}>
                  <td className="p-3 font-display font-black text-[#555]">{s.rank}</td>
                  <td className="p-3 font-bold">{s.name}</td>
                  <td className="p-3" style={{ color: CLUB_COLOR[s.club] || "#737373" }}>{s.club || "—"}</td>
                  <td className="p-3 text-[10px] uppercase tracking-widest text-[#737373]">
                    Observador
                  </td>
                  <td className="p-3 text-right font-display font-black">{s.personal_weeks}</td>
                  <td className="p-3 text-right font-display font-black text-[#3B82F6]">{s.personal_points}</td>
                  <td className="p-3 text-right text-[#E50000]">{s.nao_reportou_count || 0}</td>
                </tr>
              ))}
              {rank.length === 0 && (
                <tr><td colSpan="7" className="p-6 text-center text-[#737373]">
                  Ainda não há observadores. Define o estado em Consultores.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAdmin && events.length > 0 && (
        <div className="kw-card p-4" data-testid="roster-events">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-[#E50000]" />
            <span className="kw-overline">Histórico de trocas</span>
          </div>
          <div className="space-y-1.5">
            {events.slice(0, 10).map((e) => (
              <div key={e.id} className="text-xs text-[#A3A3A3]">
                <span className="text-[#555] font-mono">S{e.week}</span>{" "}
                {e.kind === "withdraw" ? (
                  <><b className="text-white">{e.player_name}</b> foi retirado(a) da liga{e.reason && <> · <i>{e.reason}</i></>}</>
                ) : e.to_status === "observer" ? (
                  <><b className="text-white">{e.player_name}</b> passou a Observador(a)</>
                ) : (
                  <><b className="text-white">{e.player_name}</b> entrou a coopetir
                  {e.replaced_player_name && <> no lugar de <b className="text-white">{e.replaced_player_name}</b></>}</>
                )}
                <span className="text-[#555]"> · {fmt(e.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {promote && <PromoteModal {...promote} onClose={() => setPromote(null)} onDone={refresh} />}
      {withdraw && <WithdrawModal {...withdraw} onClose={() => setWithdraw(null)} onDone={refresh} />}
    </div>
  );
}

const ROLE_PT = {
  president: "Consultor Presidente da Liga",
  coach: "Secretário do Clube",
  administrator: "Presidente do Clube",
};
const ROLE_SLOTS = [
  { key: "coach", label: "Secretário do Clube", icon: HeartHandshake, hint: "De outro perfil — traz um olhar de fora. Acompanha o plantel e entra no Ranking dos Secretários." },
  { key: "administrator", label: "Presidente do Clube", icon: Scale, hint: "Do perfil do clube. Zela pela união e pela administração do clube. Não coopete." },
];

const LeaguePresident = ({ data, isAdmin, onDone }) => {
  const [editing, setEditing] = useState(false);
  const p = data.league_president;
  const pool = CLUBS.flatMap((c) => {
    const row = data.by_club[c] || {};
    return [...(row.observer || []), ...(row.substitute || []), ...(row.competing || []),
            ...(row.leadership || [])].map((x) => ({ ...x, club: c }));
  }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  if (!p && !isAdmin) return null;
  return (
    <div className="kw-card p-5 border-[#EAB308]/40" data-testid="league-president">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="w-11 h-11 shrink-0 border border-[#EAB308] bg-[#EAB308]/10 flex items-center justify-center">
          <Crown className="w-5 h-5 text-[#EAB308]" />
        </span>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">
            Consultor Presidente da Liga · entidade máxima
          </div>
          <div className={`font-display text-2xl font-black uppercase ${p ? "" : "text-[#555] italic"}`}>
            {p ? p.name : "vago"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-0.5">
            Um só para toda a Liga, acima dos administradores dos clubes · não coopete
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(!editing)} className="kw-btn-secondary !py-2 text-[11px]"
              data-testid="assign-league-president">{p ? "Trocar" : "Definir"}</button>
            {p && (
              <button onClick={() => setRole(p.id, null, onDone)} className="text-[#555] hover:text-[#E50000] p-1"
                title="Remover função" data-testid={`unset-role-${p.id}`}>
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {isAdmin && editing && (
        <select className="kw-input !py-2 text-xs mt-4" defaultValue=""
          onChange={(e) => { if (e.target.value) { setRole(e.target.value, "president", onDone); setEditing(false); } }}
          data-testid="select-league-president">
          <option value="">Escolher consultor…</option>
          {pool.map((x) => (
            <option key={x.id} value={x.id}>{x.name} · {x.club}</option>
          ))}
        </select>
      )}
    </div>
  );
};

const setRole = async (id, role, onDone, club) => {
  try {
    await api.patch(`/players/${id}/club-role`, club ? { role, club } : { role });
    toast.success(role ? `Agora é ${ROLE_PT[role]}` : "Função do clube removida");
    onDone();
  } catch (err) { toast.error(formatApiError(err)); }
};

const CARD_META = {
  yellow: { label: "Amarelo", color: "#EAB308", desc: "Advertência" },
  red: { label: "Vermelho", color: "#E50000", desc: "Expulsão" },
  white: { label: "Branco", color: "#FFFFFF", desc: "Iniciativa que aporta valor" },
};

function CardsPanel({ roster, onDone }) {
  const [cards, setCards] = useState([]);
  const [kind, setKind] = useState("white");
  const [playerId, setPlayerId] = useState("");
  const [club, setClub] = useState("");
  const [reason, setReason] = useState("");
  const [pts, setPts] = useState(10);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/cards").then((r) => setCards(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const everyone = useMemo(() => {
    const out = [];
    CLUBS.forEach((c) => {
      const row = roster.by_club[c] || {};
      ["leadership", "competing", "substitute", "observer"].forEach((g) => {
        (row[g] || []).forEach((p) => out.push({ ...p, club: c }));
      });
    });
    return out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [roster]);

  const submit = async () => {
    try {
      const body = { kind, reason };
      if (kind === "white") {
        body.points_each = Number(pts) || 10;
        if (playerId) body.player_id = playerId;
        body.club = club || everyone.find((p) => p.id === playerId)?.club;
      } else {
        body.player_id = playerId;
      }
      const r = await api.post("/cards", body);
      toast.success(kind === "white"
        ? `Cartão branco a ${r.data.club}: ${r.data.points_each} pts para cada um dos ${r.data.beneficiaries} que coopetem (${r.data.club_points} ao clube)`
        : `Cartão ${CARD_META[kind].label.toLowerCase()} atribuído`);
      setReason(""); setPlayerId(""); setClub("");
      load(); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/cards/${id}`); toast.success("Cartão removido"); load(); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="kw-card p-0" data-testid="cards-panel">
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 hover:bg-[#111] transition-colors"
        data-testid="toggle-cards">
        <div className="flex gap-1 shrink-0">
          {["yellow", "red", "white"].map((k) => (
            <span key={k} className="w-3 h-4 rounded-[1px]" style={{ background: CARD_META[k].color }} />
          ))}
        </div>
        <span className="flex-1 text-left">
          <span className="block kw-overline text-[10px]">Cartões</span>
          <span className="block text-sm text-[#A3A3A3]">
            Amarelo avisa · Vermelho expulsa · Branco premeia quem aporta valor
          </span>
        </span>
        <span className="font-display text-2xl font-black text-[#555]">{cards.length}</span>
        <ChevronRight className={`w-4 h-4 text-[#555] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[#222] p-4 space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <label className="kw-overline text-[10px] block mb-1">Cartão</label>
              <select className="kw-input !py-2" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="card-kind">
                <option value="white">Branco — iniciativa de valor</option>
                <option value="yellow">Amarelo — advertência</option>
                <option value="red">Vermelho — expulsão</option>
              </select>
            </div>
            <div>
              <label className="kw-overline text-[10px] block mb-1">
                {kind === "white" ? "Autor da ideia (opcional)" : "Consultor"}
              </label>
              <select className="kw-input !py-2" value={playerId} onChange={(e) => setPlayerId(e.target.value)} data-testid="card-player">
                <option value="">— escolher —</option>
                {everyone.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.club})</option>)}
              </select>
            </div>
            {kind === "white" ? (
              <>
                <div>
                  <label className="kw-overline text-[10px] block mb-1">Clube premiado</label>
                  <select className="kw-input !py-2" value={club} onChange={(e) => setClub(e.target.value)} data-testid="card-club">
                    <option value="">— do autor —</option>
                    {CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="kw-overline text-[10px] block mb-1">Pontos por consultor</label>
                  <input type="number" min="1" className="kw-input !py-2" value={pts}
                    onChange={(e) => setPts(e.target.value)} data-testid="card-points" />
                  <p className="text-[9px] text-[#737373] mt-1">
                    {pts || 0} × 8 que coopetem = {(Number(pts) || 0) * 8} pts ao clube
                  </p>
                </div>
              </>
            ) : <div className="md:col-span-2" />}
          </div>
          <div>
            <label className="kw-overline text-[10px] block mb-1">Motivo</label>
            <input className="kw-input !py-2" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={kind === "white" ? "Ex: sugestão EUREKA implementada — fila de contactos automática" : "Ex: quebra da Regra de Ouro na sessão de quinta"}
              data-testid="card-reason" />
          </div>
          <div className="flex justify-end">
            <button onClick={submit} className="kw-btn-primary !py-2 !px-5 text-[10px]" data-testid="card-submit">
              Atribuir cartão {CARD_META[kind].label.toLowerCase()}
            </button>
          </div>

          {cards.length > 0 && (
            <div className="divide-y divide-[#161616] border-t border-[#222] pt-2">
              {cards.map((c) => (
                <div key={c.id} className="py-2 flex items-start gap-3 text-xs" data-testid={`card-row-${c.id}`}>
                  <span className="w-3 h-4 rounded-[1px] shrink-0 mt-0.5" style={{ background: CARD_META[c.kind]?.color }} />
                  <span className="flex-1 min-w-0">
                    <span className="block font-bold">
                      {c.kind === "white"
                        ? `${c.club} · ${c.points_each} pts × ${c.beneficiaries} = ${c.club_points}`
                        : c.player_name}
                      <span className="text-[#555] font-normal"> · S{c.week}</span>
                    </span>
                    <span className="block text-[10px] text-[#737373]">
                      {c.reason}{c.author_name && ` · ideia de ${c.author_name}`}
                    </span>
                  </span>
                  <button onClick={() => remove(c.id)} className="text-[#555] hover:text-[#E50000] p-1 shrink-0" data-testid={`card-del-${c.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Leadership = ({ people, club, isAdmin, onDone, pool = [], outsidePool = [], volunteers = {} }) => {
  const [editing, setEditing] = useState(null);
  const byRole = {};
  people.forEach((p) => { byRole[p.club_role] = p; });

  const wants = (name, role) => {
    const v = volunteers[name];
    return v === "both" || v === role;
  };

  const assign = (role, id) => {
    if (!id) return;
    setRole(id, role, onDone, club);
    setEditing(null);
  };

  if (!people.length && !isAdmin) return null;
  return (
    <div className="border-b border-[#161616]" data-testid={`leadership-${club}`}>
      <div className="px-3 py-2 text-[9px] uppercase tracking-[0.2em] font-bold text-[#EAB308] flex items-center gap-2">
        <Crown className="w-3 h-3" />
        <span className="flex-1">Liderança do clube · cargos</span>
        <span className="text-[#555]">{people.length}/2</span>
      </div>

      {ROLE_SLOTS.map((slot) => {
        const holder = byRole[slot.key];
        if (!holder && !isAdmin) return null;
        return (
          <div key={slot.key} className="px-3 py-2 hover:bg-[#111]" data-testid={`slot-${slot.key}-${club}`}>
            <div className="flex items-center gap-2 text-xs">
              <slot.icon className="w-3.5 h-3.5 text-[#EAB308] shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-[9px] text-[#EAB308] uppercase tracking-widest">{slot.label}</span>
                <span className={`block truncate ${holder ? "font-bold" : "text-[#555] italic"}`}>
                  {holder ? holder.name : "vago"}
                  {holder && slot.key === "coach" && holder.own_club && holder.own_club !== club && (
                    <span className="text-[#737373] font-normal"> · {holder.own_club}</span>
                  )}
                </span>
              </span>
              {isAdmin && (
                <>
                  <button onClick={() => setEditing(editing === slot.key ? null : slot.key)}
                    className="text-[#737373] hover:text-white text-[9px] uppercase tracking-widest px-2 py-1 border border-[#262626] shrink-0"
                    data-testid={`assign-${slot.key}-${club}`}>
                    {holder ? "Trocar" : "Definir"}
                  </button>
                  {holder && (
                    <button onClick={() => setRole(holder.id, null, onDone)} className="text-[#555] hover:text-[#E50000] p-1 shrink-0"
                      title="Remover função" data-testid={`unset-role-${holder.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
            {isAdmin && editing === slot.key && (() => {
              const source = slot.key === "coach" ? outsidePool : pool;
              const keen = source.filter((p) => wants(p.name, slot.key));
              const rest = source.filter((p) => !wants(p.name, slot.key));
              const label = slot.key === "coach"
                ? `Consultores de outros perfis (não ${club})`
                : `Consultores de ${club}`;
              return (
                <div className="mt-2 space-y-1">
                  <div className="text-[9px] uppercase tracking-widest text-[#555]">{slot.hint}</div>
                  {keen.length > 0 && (
                    <div className="text-[9px] uppercase tracking-widest text-[#EAB308]"
                      data-testid={`volunteers-${slot.key}-${club}`}>
                      {keen.length} disponibilizou-se na candidatura
                    </div>
                  )}
                  <select className="kw-input !py-1.5 text-xs" defaultValue=""
                    onChange={(e) => assign(slot.key, e.target.value)}
                    data-testid={`select-${slot.key}-${club}`}>
                    <option value="">Escolher…</option>
                    {keen.length > 0 && (
                      <optgroup label="Disponibilizaram-se na candidatura">
                        {keen.map((p) => (
                          <option key={p.id} value={p.id}>
                            ★ {p.name}{p.mbti ? ` · ${p.mbti}` : ""}{p.own_club && slot.key === "coach" ? ` · ${p.own_club}` : ""}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label={label}>
                      {rest.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.mbti ? ` · ${p.mbti}` : ""}{p.own_club && slot.key === "coach" ? ` · ${p.own_club}` : ""}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
};

const setSince = async (p, done) => {
  const v = window.prompt(`${p.name}: a partir de que semana conta para o ranking?`, String(p.competing_since_week || 1));
  if (v === null) return;
  const week = parseInt(v, 10);
  if (!week || week < 1) { toast.error("Semana inválida"); return; }
  try {
    await api.patch(`/players/${p.id}/competing-since`, { week });
    toast.success(`${p.name} conta desde a Semana ${week}`);
    done?.();
  } catch (err) { toast.error(formatApiError(err)); }
};

const Group = ({ title, color, people, action, collapsed, onRole, onWithdraw, onSince }) => {
  const [open, setOpen] = useState(!collapsed);
  return (
    <div className="border-b border-[#161616] last:border-0">
      <button onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 flex items-center gap-2 text-[9px] uppercase tracking-[0.2em] font-bold hover:bg-[#111] transition-colors"
        style={{ color }}>
        <span className="flex-1 text-left">{title}</span>
        <span className="text-[#555]">{people.length}</span>
      </button>
      {open && people.map((p) => (
        <div key={p.id} className="px-3 py-2 flex items-center gap-2 text-xs hover:bg-[#111]">
          <span className="flex-1 min-w-0">
            <span className="block font-bold truncate">{p.name}</span>
            <span className="block text-[9px] text-[#737373]">
              {p.mbti || "—"} · MBTI {fmt(p.mbti_answered_at)}
              {onSince && (
                <button onClick={() => setSince(p, onSince)} className="ml-1 underline decoration-dotted hover:text-white"
                  title="Corrigir a semana a partir da qual conta para o ranking" data-testid={`since-week-${p.id}`}>
                  · conta desde a S{p.competing_since_week || 1}
                </button>
              )}
            </span>
          </span>
          {onRole && (
            <>
              <button onClick={() => setRole(p.id, "president", onRole)} className="text-[#EAB308] hover:bg-[#EAB308]/10 p-1 shrink-0"
                title="Tornar Consultor Presidente" data-testid={`set-president-${p.id}`}>
                <Crown className="w-4 h-4" />
              </button>
              <button onClick={() => setRole(p.id, "coach", onRole)} className="text-[#EAB308] hover:bg-[#EAB308]/10 p-1 shrink-0"
                title="Tornar Secretário do Clube" data-testid={`set-club-coach-${p.id}`}>
                <HeartHandshake className="w-4 h-4" />
              </button>
              <button onClick={() => setRole(p.id, "administrator", onRole)} className="text-[#EAB308] hover:bg-[#EAB308]/10 p-1 shrink-0"
                title="Tornar Presidente do Clube" data-testid={`set-club-admin-${p.id}`}>
                <Scale className="w-4 h-4" />
              </button>
            </>
          )}
          {onWithdraw && (
            <button onClick={() => onWithdraw(p)} className="text-[#737373] hover:text-[#E50000] hover:bg-[#E50000]/10 p-1 shrink-0"
              title="Retirar da liga" data-testid={`withdraw-${p.id}`}>
              <ArrowDownCircle className="w-4 h-4" />
            </button>
          )}
          {action && (
            <button onClick={() => action(p)} className="text-[#22C55E] hover:bg-[#22C55E]/10 p-1 shrink-0"
              title="Promover a coopetir" data-testid={`promote-${p.id}`}>
              <ArrowUpCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

function WithdrawModal({ player, club, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/players/${player.id}/withdraw`, { reason: reason || null });
      toast.success(`${r.data.withdrawn} saiu da liga e passa a Observador(a)`);
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="kw-card max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="withdraw-modal">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="kw-overline text-[10px]">{CLUB_EMOJI[club]} {club}</div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight">Retirar da liga</h2>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#A3A3A3] mb-4">
          <b className="text-white">{player.name}</b> deixa de contar para o campeonato e passa a Observador(a).
          Mantém todo o histórico e pode voltar a ser promovido(a). A vaga fica livre.
        </p>
        <label className="kw-overline text-[10px] block mb-1">Motivo (opcional)</label>
        <input className="kw-input !py-2" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)}
          placeholder="Ex.: não pode participar" data-testid="withdraw-reason" />
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white"
            data-testid="withdraw-cancel">
            Cancelar
          </button>
          <button onClick={go} disabled={saving} className="kw-btn-primary !py-2 !px-5 text-[10px]" data-testid="withdraw-confirm">
            {saving ? "A retirar…" : "Retirar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromoteModal({ player, club, competing, onClose, onDone }) {
  const [replacing, setReplacing] = useState("");
  const [week, setWeek] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try {
      const body = {};
      if (replacing) body.replacing_player_id = replacing;
      if (week) body.from_week = Number(week);
      const r = await api.post(`/players/${player.id}/promote`, body);
      toast.success(`${r.data.promoted} entra a coopetir na semana ${r.data.week}${r.data.replaced ? ` (substitui ${r.data.replaced})` : ""}`);
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="kw-card max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="promote-modal">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="kw-overline text-[10px]">{CLUB_EMOJI[club]} {club}</div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight">Promover a coopetir</h2>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#A3A3A3] mb-4">
          <b className="text-white">{player.name}</b> passa a contar para o campeonato a partir da semana que
          escolheres. A accountability pessoal dele mantém <b className="text-white">todos</b> os pontos que já fez.
        </p>
        <div className="space-y-3">
          <div>
            <label className="kw-overline text-[10px] block mb-1">Entra em vez de (opcional)</label>
            <select className="kw-input !py-2" value={replacing} onChange={(e) => setReplacing(e.target.value)}
              data-testid="promote-replacing">
              <option value="">— Ninguém, é uma vaga livre —</option>
              {competing.map((p) => <option key={p.id} value={p.id}>{p.name}{p.expelled ? " (expulso)" : ""}</option>)}
            </select>
            <p className="text-[10px] text-[#737373] mt-1">Quem sair passa a Observador e mantém o seu histórico.</p>
          </div>
          <div>
            <label className="kw-overline text-[10px] block mb-1">A partir da semana (vazio = semana atual)</label>
            <input type="number" min="1" className="kw-input !py-2" value={week}
              onChange={(e) => setWeek(e.target.value)} placeholder="Semana atual" data-testid="promote-week" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white">
            Cancelar
          </button>
          <button onClick={go} disabled={saving} className="kw-btn-primary !py-2 !px-5 text-[10px]" data-testid="promote-confirm">
            {saving ? "A promover…" : "Promover"}
          </button>
        </div>
      </div>
    </div>
  );
}
